import { istAbgeleitet, istMarkierebene, istVideoAnnotation } from '../domain/abgeleitet';
import { artAusPfad, keineDateien, type Dateiart } from '../domain/rueckstand';
import {
  ABLEHNUNGSGRENZE,
  istAblehnung,
  istAufgegeben,
  istFaellig,
  merkeErfolg,
  merkeFehlschlag,
  staende,
} from '../persistence/anhaenge';
import type { Store } from '../persistence/port';
import { appendAppLog } from '../platform/appLog';
import { listFiles, readBase64, readText, type LogKind, type LogScope } from '../platform/storage';
import {
  felderAusName,
  mimeVonName,
  multipartSender,
  type MultipartSender,
} from './multipart';

/**
 * Die Dateien gehen im **Relay**-Modus hinaus.
 *
 * **Der Vertrag ist eine Festlegung der App, kein Serverbefund.** Am 2026-09-13 im
 * Relay-Quelltext nachgesehen: `ExamLogService` nimmt nur JSON, `MediaService` kennt nur den
 * Hinweg — einen Endpunkt für Aufnahmen gibt es nicht. Er steht in
 * `docs/spec-relay-etappe5-dateien.md` §3 und wird dort gebaut; weicht er ab, ändert sich
 * dieses Modul, und der Durchstich-Test fällt darauf.
 *
 * ⚠️ **Die Protokollzeile nennt die Datei nicht.** Seit dem 2026-09-14 trägt der `value` der
 * Medienzeilen nur noch die **Anzahl** der Aufnahmen (`domain/medienzahl.ts`); der Server ordnet
 * die Anhänge über ihre eigenen Felder zu. Die Markierebene und das Vorschaubild haben deshalb
 * gar keine Zeile — die Ebene gehört zu ihrem Foto, das Vorschaubild geht überhaupt nicht hinaus.
 *
 * ⚠️ **Die `.sig` fährt als Feld mit, nicht als zweiter Dateiteil.** Sie ist gewöhnliches JSON
 * von ein bis zwei Kilobyte, und der native Weg trägt **eine** Datei je Aufruf. Wörtlich
 * weitergereicht: geprüft wird über genau diese Bytes, und ein Umweg über `JSON.parse` machte
 * jede gültige Unterschrift ungültig (`docs/beglaubigung.md`).
 *
 * ⚠️ **Anders als Classic.** Dort bleibt die Beglaubigung am Gerät, weil der Classic-Server sie
 * nicht deuten kann (`classic/anforderungen.ts`). Hier gehört sie zum Beleg
 * (`memory/zweigleisig.md`).
 */

export const ANHANG_PFAD = '/rest/v1/exam-log/attachment';

/**
 * Die Arten der Ablage, dazu die beiden Ebenen — sie sind keine eigene `LogKind`.
 *
 * `overlay` ist die Markierebene eines Fotos (SVG), `annotations` die zeitlich begrenzte
 * Ebene einer Videoaufnahme (JSON, `domain/videoAnnotation.ts`).
 */
export type AnhangArt = LogKind | 'overlay' | 'annotations';

export interface OffeneDatei {
  /** Pfad unterhalb von Documents, wie ihn `platform/storage.ts` zurückgibt. */
  pfad: string;
  art: AnhangArt;
}

/** Alle Bezüge, in denen Dateien liegen können (`platform/storage.ts`). */
const BEZUEGE: LogScope[] = ['station', 'station-comment', 'task-comment'];

/**
 * Was der Prüfer aufnimmt. Absichtlich `LogKind` vollständig — kommt eine Art dazu, fährt sie
 * von selbst mit.
 */
const ARTEN: LogKind[] = ['screenshot', 'photo', 'audio', 'video', 'handwriting'];

/*
 * ⚠️ **Das Vorschaubild einer Videoaufnahme geht nicht hinaus.** Es ist abgeleitet, nicht
 * aufgenommen: ein erster Einzelbild-Abzug des Videos, damit die Liste am Gerät etwas zu zeigen
 * hat. Es trägt keine Beglaubigung, und zu ihm gehört keine Protokollzeile — auf dem Server
 * wäre es eine Datei ohne Anker. Die Markierebene eines Fotos und die Annotationsebene einer
 * Videoaufnahme sind dagegen Inhalt und fahren als eigene Arten mit; die Endungen selbst stehen
 * in `domain/abgeleitet.ts`.
 */

/** Nur der Dateiname, ohne Ordner. */
const nameVon = (pfad: string) => pfad.slice(pfad.lastIndexOf('/') + 1);

/**
 * Alles, was der Prüfer aufgenommen hat.
 *
 * Die Beglaubigungen bleiben hier aussen vor — sie fahren **mit ihrer Datei**, nicht als
 * eigene Übertragung. Die Markierebenen liegen im Ordner ihres Fotos und bekommen deshalb hier
 * ihre eigene Art; auf dem Server ist die Ebene Inhalt, kein Beweis.
 */
export async function sammleAnhaenge(): Promise<OffeneDatei[]> {
  const gefunden: OffeneDatei[] = [];
  for (const bezug of BEZUEGE) {
    for (const art of ARTEN) {
      for (const pfad of await listFiles(bezug, art)) {
        const ebene = istMarkierebene(pfad)
          ? 'overlay'
          : istVideoAnnotation(pfad)
            ? 'annotations'
            : null;
        if (istAbgeleitet(pfad) && ebene === null) continue;
        gefunden.push({ pfad, art: ebene ?? art });
      }
    }
  }
  return gefunden;
}

/**
 * Ob eine abgelegte Datei überhaupt nach Relay gehört.
 *
 * ⚠️ **Im Classic-Modus tragen unsere Kennungen Doppelpunkte** — aus der Aufgabe `123` wird
 * `classic:task:123` (`classic/kennung.ts`), und `buildFileName` bereinigt nur `_` und `=`.
 * Nach einem Moduswechsel liegen solche Dateien in derselben Ablage wie die Relay-Belege.
 *
 * Sie gehören dem Classic-Server: dort sind ihre Kennungen gültig, hier nicht. Ohne diese Sperre
 * gingen sie an Relay, würden mit `BAD_FILE_PATH` abgewiesen (der Server verbietet den
 * Doppelpunkt im Pfad) und nach fünf Anläufen aufgegeben — der Prüfer sähe eine Warnung über
 * abgewiesene Dateien, die nie an diese Adresse gerichtet waren.
 *
 * Geprüft wird auf den Doppelpunkt und nicht auf `classic:`: der Server nimmt ihn in keiner
 * Form, egal wer ihn erzeugt hat.
 */
export function istRelayTauglich(pfad: string): boolean {
  return !pfad.includes(':');
}

/**
 * Unsere Zeitmarke in die Relay-Form.
 *
 * Die Ablage schreibt `yyyy-MM-dd-HH-mm-ss-SSS`, Relay verlangt `char(17)` ohne Striche
 * (`persistence/relay.ts`). Die Ablage bleibt, wie sie ist; umgerechnet wird beim Senden.
 */
export function relayZeitstempel(unsere: string): string {
  return unsere.replace(/-/g, '');
}

/**
 * Die Felder neben der Datei.
 *
 * ⚠️ **Die Markierebene trägt den Namen ihres Fotos plus `-overlay.svg`.** Sie wird nicht hier
 * zugeschnitten: `felderAusName` schneidet jede Ableitung selbst ab (seit 2026-09-14, s.
 * `domain/abgeleitet.ts`) — vorher stand die Wache nur hier, und das Classic-Gleis schickte die
 * Ebene eines Stationskommentars ohne Station hinaus. `fileName` und `filePath` bleiben
 * unangetastet: der Server legt die Datei unter genau diesem Namen ab.
 */
export function baueAnhangFelder(args: {
  pfad: string;
  art: AnhangArt;
  signatur: string | null;
}): Record<string, string> {
  const dateiname = nameVon(args.pfad);
  const ausName = felderAusName(dateiname);

  const felder: Record<string, string> = {
    kind: args.art,
    fileName: dateiname,
    /*
     * ⚠️ **Der Pfad, nicht nur der Name.** Der Server legt die Datei darunter ab
     * (`<Wurzel>/attachments/<examID>/<filePath>`); zwei Aufnahmen verschiedener Art können
     * denselben blossen Namen tragen, der Pfad hält sie auseinander.
     *
     * ⚠️ **Er stand bis zum 2026-09-14 auch in `ExamLog.value`** — daher stammt das Feld. Der
     * `value` trägt jetzt die **Anzahl** der Aufnahmen (`domain/medienzahl.ts`), das Feld
     * bleibt aber: es kommt ohnehin aus dem Dateisystem (`sammleAnhaenge`), nicht aus dem
     * Protokoll, und der Server nimmt es unverändert an.
     */
    filePath: args.pfad,
    creationDate: relayZeitstempel(ausName.date ?? ''),
  };
  /*
   * Leere Felder bleiben weg: ein `taskID=` ohne Wert sähe beim Server wie eine Zuordnung aus,
   * die es nicht gibt. Die Reihenfolge ist die des Vertrags (Spec §3).
   *
   * ⚠️ **`actorID` und `itemID` kamen am 2026-09-14 dazu**, zusammen mit der neuen Namensform
   * (`platform/storage.ts::buildFileName`). Seit derselben Änderung trägt der `value` der
   * Protokollzeile nur noch die Anzahl der Aufnahmen — der Name ist damit die einzige
   * Zuordnung, die der Server hat, und was hier nicht durchgereicht wird, ist für ihn verloren.
   *
   * ⚠️ **Ältere Dateien tragen die Felder nicht.** Sie fallen still heraus, statt leer zu
   * fahren — dieselbe Wache wie bisher.
   */
  for (const schluessel of ['examID', 'candidateID', 'examinerID', 'actorID']) {
    if (ausName[schluessel]) felder[schluessel] = ausName[schluessel];
  }
  /*
   * ⚠️ **Station und Aufgabe gehen in Relay' Begriffen hinaus**, gleich welche Form im Namen
   * steht (Vorgabe des Nutzers, 2026-09-17): `examItemContainerID` ist unsere Station,
   * `examItemID` unsere Aufgabe. Ältere Dateien und solche aus dem Classic-Gleis tragen
   * `stationID`/`taskID` (ganz alte zusätzlich `itemID`) — sie werden hier übersetzt, statt
   * unter ihrem alten Namen zu fahren.
   *
   * ⚠️ **Keine Mischung in einer Anfrage** (Absprache mit der Relay-Sitzung): entweder die
   * neuen Feldnamen oder die alten, nie beide. Kämen beide, gewänne dort die neue Form — das
   * wäre stilles Raten statt einer Zuordnung.
   *
   * ⚠️ **Fehlt die Aufgabe, fehlt das Feld.** Kein `-1`: der Server fällt dann von selbst auf
   * die Container-Ebene zurück, und ein Sonderwert müsste drüben eigens abgefangen werden.
   */
  const station = ausName.examItemContainerID ?? ausName.stationID;
  const aufgabe = ausName.examItemID ?? ausName.taskID ?? ausName.itemID;
  if (station) felder.examItemContainerID = station;
  if (aufgabe) felder.examItemID = aufgabe;
  if (args.signatur) felder.signature = args.signatur;
  return felder;
}

export interface AnhaengeDeps {
  store: Store;
  /** Der Server, ohne abschliessenden Schrägstrich. */
  basisUrl: string;
  /** Das Bearer-Token der laufenden Sitzung — nur zur Meldung im Protokoll. */
  token: string;
  senden: MultipartSender;
  /** Einschiebbar für Tests; sonst die echte Ablage. */
  sammle?: () => Promise<OffeneDatei[]>;
  lese?: (pfad: string) => Promise<string>;
  leseText?: (pfad: string) => Promise<string>;
  /**
   * Das Dateilog. `stufe` bestimmt, unter welcher Konsolenfunktion die Zeile am Kabel in
   * Xcode erscheint (s. `platform/appLog.ts`); in der Datei sieht jede Zeile gleich aus.
   */
  log?: (zeile: string, stufe?: 'warn' | 'error') => Promise<void>;
  jetzt?: () => Date;
}

/**
 * Ein Durchgang über die offenen Dateien.
 *
 * **Nacheinander, nicht gleichzeitig**: ein iPad mit vier gleichzeitigen 30-MB-Strömen an einer
 * Prüfungs-WLAN-Zelle hilft niemandem. **Ein Fehlschlag hält die übrigen nicht auf** — er
 * verschiebt nur den nächsten Versuch dieser einen Datei (`persistence/anhaenge.ts`).
 *
 * Der Aufrufer entscheidet über den Takt; dieses Modul kennt keinen Zeitgeber. So lässt es sich
 * prüfen, ohne auf Uhren zu warten.
 */
export async function uebertrageAnhaenge(deps: AnhaengeDeps): Promise<{
  uebertragen: number;
  liegengeblieben: number;
  /** Dateien einer Simulationsprüfung: örtlich abgehakt, nichts gesendet. */
  simuliert: number;
}> {
  const sammle = deps.sammle ?? sammleAnhaenge;
  const lese = deps.lese ?? readBase64;
  const leseText = deps.leseText ?? readText;
  const log = deps.log ?? appendAppLog;
  const jetzt = deps.jetzt ?? (() => new Date());
  const url = deps.basisUrl.replace(/\/+$/, '') + ANHANG_PFAD;

  const stand = await staende(deps.store);
  const alle = await sammle();

  // Fremde Belege gar nicht erst anfassen — sie bekommen auch keine Zeile: sie sind nicht
  // gescheitert, sie gehören nur woandershin.
  const fremde = alle.filter((datei) => !istRelayTauglich(datei.pfad));
  if (fremde.length > 0) {
    await log(
      'Anhänge: ' + String(fremde.length) + ' Datei(en) aus dem Classic-Modus bleiben liegen — ' +
        'ihre Kennungen gelten nur am Classic-Server.',
      'warn',
    );
  }

  const faellig = alle.filter(
    (datei) => istRelayTauglich(datei.pfad) && istFaellig(stand.get(datei.pfad), jetzt()),
  );

  let uebertragen = 0;
  let liegengeblieben = 0;
  let simuliert = 0;

  /**
   * ⛔ **Dateien einer Simulationsprüfung gehen gar nicht los** (Entscheidung des Nutzers,
   * 2026-09-18).
   *
   * ⬅️ **Richtigstellung vom 2026-09-18:** hier stand, der Server lese die Datei nur, um sie
   * wegzuwerfen. Dieser Wächter ist am selben Tag wieder entfernt worden. Eine Aufnahme, die
   * hinausginge, **bleibt** dort liegen — bei einer Schulung wären das Übungsaufnahmen in der
   * echten Prüfungsablage.
   *
   * ⚠️ **Abgehakt, nicht liegengelassen** — sonst zählte „Alle Dateien übertragen" nie fertig.
   * Die Datei selbst bleibt auf dem Gerät und wird vom Archiv nach der Aufbewahrungsfrist
   * geräumt wie jede andere; nur hinausgeschickt wird sie nie.
   */
  const simulativeIds = new Set(
    (await deps.store.all<{ id: string; simulative?: string }>('Exam'))
      .filter((e) => e.simulative === 'Y')
      .map((e) => e.id),
  );

  const offen: typeof faellig = [];
  for (const datei of faellig) {
    // Nur die `examID` wird gebraucht; die Beglaubigung bleibt ungelesen, weil sie am Ziel
    // nichts ändert und ein Lesezugriff je Datei umsonst wäre.
    const felder = baueAnhangFelder({ pfad: datei.pfad, art: datei.art, signatur: null });
    if (felder.examID && simulativeIds.has(felder.examID)) {
      await merkeErfolg(deps.store, {
        pfad: datei.pfad,
        examID: felder.examID,
        art: datei.art,
        jetzt: jetzt(),
      });
      simuliert += 1;
      continue;
    }
    offen.push(datei);
  }

  if (simuliert > 0) {
    await log(
      'Simulationsprüfung: ' + String(simuliert) +
        ' Datei(en) örtlich abgehakt, nichts gesendet (nichts wird aufgezeichnet).',
    );
  }

  for (const datei of offen) {
    const dateiname = nameVon(datei.pfad);
    // Fehlt die Beglaubigung, geht die Datei trotzdem hinaus: ein Beleg ohne Siegel auf dem
    // Server ist besser als einer, der für immer auf dem iPad bleibt.
    const signatur = await leseText(datei.pfad + '.sig').catch(() => null);
    const felder = baueAnhangFelder({ pfad: datei.pfad, art: datei.art, signatur });
    const gemeinsam = { pfad: datei.pfad, examID: felder.examID ?? null, art: datei.art };

    try {
      const base64 = await lese(datei.pfad);
      const antwort = await deps.senden({
        url,
        felder,
        datei: { name: dateiname, mime: mimeVonName(dateiname), base64 },
      });

      if (!antwort.ok) {
        liegengeblieben += 1;
        /*
         * ⚠️ **Nur 4xx zählt auf die Obergrenze.** Ein 5xx heisst „der Server ist kaputt",
         * nicht „diese Datei taugt nicht" — sie aufzugeben hiesse, sie für einen Fehler zu
         * bestrafen, der auf der anderen Seite liegt. Dieselbe Trennung trifft `client.ts`
         * mit `lohntWiederholung`.
         */
        const abgewiesen = istAblehnung(antwort.status);
        await merkeFehlschlag(deps.store, {
          ...gemeinsam,
          fehler: 'Status ' + String(antwort.status),
          jetzt: jetzt(),
          abgewiesen,
        });
        await log('Anhang abgelehnt: ' + dateiname + ' — Status ' + String(antwort.status), 'warn');

        if (abgewiesen && istAufgegeben((await staende(deps.store)).get(datei.pfad))) {
          // Die Zeile steht hier und nicht in `persistence/anhaenge.ts`: dort wird gerechnet,
          // hier wird gehandelt — und aufgegeben wird eine Datei genau einmal.
          await log(
            'Anhang aufgegeben nach ' + String(ABLEHNUNGSGRENZE) + ' Ablehnungen: ' + dateiname +
              ' — sie wird nicht mehr geschickt. Der Server weist sie dauerhaft ab.',
            'error',
          );
        }
        continue;
      }

      await merkeErfolg(deps.store, { ...gemeinsam, jetzt: jetzt() });
      uebertragen += 1;
    } catch (fehler) {
      liegengeblieben += 1;
      await merkeFehlschlag(deps.store, { ...gemeinsam, fehler: String(fehler), jetzt: jetzt() });
      await log('Anhang gescheitert: ' + dateiname + ' — ' + String(fehler), 'warn');
    }
  }

  if (uebertragen > 0 || liegengeblieben > 0) {
    await log(
      'Anhänge (Relay): ' + String(uebertragen) + ' übertragen' +
        (liegengeblieben > 0 ? ', ' + String(liegengeblieben) + ' liegengeblieben' : '') + '.',
    );
  }
  return { uebertragen, liegengeblieben, simuliert };
}

/**
 * Was noch aussteht — für die Zeile in den Einstellungen.
 *
 * `offen` zählt ohne Rücksicht auf den Rückzug: dem Prüfer sagt die Zahl, was noch aussteht,
 * nicht, was in dieser Sekunde an der Reihe wäre. `abgewiesen` zählt die Dateien, die der
 * Server dauerhaft ablehnt — sie stehen **nicht** in `offen`, denn sie warten auf nichts mehr.
 * Sie liegen trotzdem weiter auf dem Gerät; nur der Weg zum Server ist zu.
 */
export async function anhangStand(
  store: Store,
  sammle: () => Promise<OffeneDatei[]> = sammleAnhaenge,
): Promise<{ offen: number; abgewiesen: number; jeArt: Record<Dateiart, number> }> {
  const stand = await staende(store);
  let offen = 0;
  let abgewiesen = 0;
  const jeArt = keineDateien();
  for (const datei of await sammle()) {
    // Classic-Belege stehen in keiner der beiden Zahlen: sie warten nicht, sie sind auch
    // nicht abgewiesen — sie sind schlicht nicht für diesen Server bestimmt.
    if (!istRelayTauglich(datei.pfad)) continue;
    const zeile = stand.get(datei.pfad);
    if (zeile?.erledigt === 'Y') continue;
    if (istAufgegeben(zeile)) abgewiesen += 1;
    else {
      offen += 1;
      jeArt[artAusPfad(datei.pfad)] += 1;
    }
  }
  return { offen, abgewiesen, jeArt };
}

/** Der Transport für **Relay**: derselbe wie überall, nur mit dem Bearer-Token davor. */
export function relaySender(
  token: string,
  optionen: { nativ?: boolean; hole?: typeof fetch } = {},
): MultipartSender {
  return multipartSender({ Authorization: 'Bearer ' + token }, optionen);
}
